import { supabase } from './supabase';
import { v4 as uuidv4 } from 'uuid';
import { normalizeUsernameInput, validateNormalizedUsername } from './username';

export interface HeroLink {
  id: string;
  url: string;
  label?: string | null;
}

export interface Profile {
  id: string;
  username: string;
  full_name: string | null;
  bio: string | null;
  website: string | null;
  avatar_path: string | null;
  country: 'US' | 'CA';
  default_currency: 'usd' | 'cad';
  show_avatar: boolean;
  hero_style: string;
  hero_banner_path: string | null;
  hero_text_color: 'light' | 'dark';
  section_theme: string;
  hero_links: HeroLink[];
  created_at: string;
  updated_at: string;
}

export function resolveHeroLinks(profile: Pick<Profile, 'hero_links' | 'website'>): HeroLink[] {
  const links = Array.isArray(profile.hero_links) ? profile.hero_links : [];
  const cleaned = links.filter((l) => l && typeof l.url === 'string' && l.url.trim().length > 0);
  if (cleaned.length > 0) return cleaned;
  if (profile.website && profile.website.trim().length > 0) {
    return [{ id: 'legacy-website', url: profile.website, label: null }];
  }
  return [];
}

export interface CreateProfileData {
  username: string;
  full_name?: string;
  bio?: string;
  website?: string;
  country: 'US' | 'CA';
  default_currency: 'usd' | 'cad';
}

export interface UpdateProfileData extends Partial<CreateProfileData> {
  id: string;
}

function isTruthyMetadataFlag(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value !== 'string') return false;
  return ['true', 't', '1', 'yes', 'y'].includes(value.trim().toLowerCase());
}

export async function ensureCurrentUserProfile(): Promise<Profile | null> {
  const { data, error } = await supabase.rpc('ensure_current_user_profile');

  if (error) {
    console.error('Error ensuring current user profile:', error);
    throw new Error('Failed to repair profile');
  }

  const profile = Array.isArray(data) ? data[0] ?? null : data;
  return profile as Profile | null;
}

export async function fetchProfile(userId: string): Promise<Profile> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    const isMissingProfile = error.code === 'PGRST116';

    if (isMissingProfile) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        const shouldSkipProfileRepair = isTruthyMetadataFlag(user?.user_metadata?.skip_profile_creation);

        if (user?.id === userId && !shouldSkipProfileRepair) {
          const repairedProfile = await ensureCurrentUserProfile();
          if (repairedProfile?.id === userId) return repairedProfile;
        }
      } catch (repairError) {
        console.error('Error repairing missing profile:', repairError);
      }
    }

    console.error('Error fetching profile:', error);
    throw new Error('Failed to load profile');
  }

  return data;
}

export async function uploadAvatar(file: File): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Validate file type
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    throw new Error('Invalid image type. Please use JPG, PNG, GIF, or WebP.');
  }

  // Validate file size (max 2MB)
  if (file.size > 2 * 1024 * 1024) {
    throw new Error('Image must be less than 2MB');
  }

  const fileExt = file.name.split('.').pop();
  const fileName = `${user.id}/${uuidv4()}.${fileExt}`;

  const { error: uploadError, data } = await supabase.storage
    .from('avatars')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false
    });

  if (uploadError) {
    console.error('Storage upload error:', uploadError);
    throw new Error(uploadError.message);
  }

  return data.path;
}

export async function updateProfile(profile: Partial<Profile> & { id: string }): Promise<Profile> {
  const updatePayload: Partial<Profile> & { updated_at: string } = {
    ...profile,
    updated_at: new Date().toISOString(),
  };

  if (typeof profile.username === 'string') {
    const normalizedUsername = normalizeUsernameInput(profile.username);
    const usernameError = validateNormalizedUsername(normalizedUsername);
    if (usernameError) throw new Error(usernameError);
    updatePayload.username = normalizedUsername;
  }

  const { data, error } = await supabase
    .from('profiles')
    .update(updatePayload)
    .eq('id', profile.id)
    .select()
    .single();

  if (error) {
    console.error('Error updating profile:', error);
    if (error.code === '23505') {
      throw new Error('That storefront URL is already taken. Please choose another one.');
    }
    throw new Error('Failed to update profile');
  }

  return data;
}

export async function fetchProfileByUsername(username: string): Promise<Profile> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('username', normalizeUsernameInput(username))
    .single();

  if (error) {
    console.error('Error fetching profile by username:', error);
    throw new Error('Profile not found');
  }

  return data;
}

export function getAvatarUrl(path: string | null): string | null {
  if (!path) return null;

  const { data } = supabase.storage
    .from('avatars')
    .getPublicUrl(path);

  return data.publicUrl;
}

export async function uploadStorefrontBanner(file: File): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    throw new Error('Invalid image type. Please use JPG, PNG, or WebP.');
  }

  if (file.size > 5 * 1024 * 1024) {
    throw new Error('Banner must be less than 5MB');
  }

  const fileExt = file.name.split('.').pop();
  const fileName = `${user.id}/${uuidv4()}.${fileExt}`;

  const { error: uploadError, data } = await supabase.storage
    .from('storefront-banners')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    console.error('Banner upload error:', uploadError);
    throw new Error(uploadError.message);
  }

  return data.path;
}

export function getStorefrontBannerUrl(path: string | null): string | null {
  if (!path) return null;

  const { data } = supabase.storage
    .from('storefront-banners')
    .getPublicUrl(path);

  return data.publicUrl;
}

export async function removeStorefrontBanner(path: string): Promise<void> {
  const { error } = await supabase.storage
    .from('storefront-banners')
    .remove([path]);

  if (error) {
    console.error('Banner removal error:', error);
  }
}
